import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Services.css';

const Services = () => {
  const servicesHeroBackgroundRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (servicesHeroBackgroundRef.current) {
        const scrolled = window.pageYOffset;
        const parallax = scrolled * 0.5;
        servicesHeroBackgroundRef.current.style.transform = `translateY(${parallax}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pillars = [
    {
      title: 'Reflection & Alignment: The Visionary Foundation',
      intro:
        'We begin by grounding your giving in clarity. We help you move past "reactive" check-writing to discover the core "why" behind your philanthropy.',
      bullets: [
        {
          label: 'Discovery & Learning',
          text: 'Immersive research into the issue areas and communities you care about most.'
        },
        {
          label: 'Vision Clarification',
          text: 'Facilitated sessions to define your long-term goals and philanthropic identity.'
        }
      ]
    },
    {
      title: 'Intentional Values-Based Strategy: The Strategic Roadmap',
      intro:
        'Once your vision is clear, we build the architecture to achieve it. We identify the highest-leverage opportunities to deploy your capital.',
      bullets: [
        {
          label: 'Purposeful Planning',
          text: 'Developing multi-year strategies that align with your unique values.'
        },
        {
          label: 'Opportunity Identification',
          text: 'Sourcing and vetting high-impact organizations and innovative social solutions.'
        }
      ]
    },
    {
      title: 'Facilitation & Collaboration: The Human Connection',
      intro:
        'Philanthropy is a team sport. We act as the central hub, ensuring your family and professional advisors are moving in the same direction.',
      bullets: [
        {
          label: 'Family Engagement',
          text: 'Facilitating intergenerational meetings to define legacy and engage the next generation.'
        },
        {
          label: 'Advisor Integration',
          text: 'Seamless collaboration with your legal and financial teams to ensure tax and estate alignment.'
        }
      ]
    },
    {
      title: 'Effectiveness & Management: The Operational Engine',
      intro:
        'We handle the "heavy lifting" of foundation life, ensuring your operations are professional, compliant, and results-oriented.',
      bullets: [
        {
          label: 'Administrative Management',
          text: 'Oversight of day-to-day operations, grant cycles, and regulatory requirements.'
        },
        {
          label: 'Impact Evaluation',
          text: 'Rigorous assessment of grant performance to ensure your resources are creating "Catalytic Change."'
        }
      ]
    }
  ];

  const renderPillarTitle = (title) => {
    const colonIndex = title.indexOf(':');
    if (colonIndex === -1) return title;

    const before = title.slice(0, colonIndex + 1);
    const after = title.slice(colonIndex + 1).trimStart();
    return (
      <>
        {before}
        <br />
        {after}
      </>
    );
  };

  return (
    <div className="services">
      <section className="services-hero">
        <div
          ref={servicesHeroBackgroundRef}
          className="services-hero-background"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/services_hero.png)`
          }}
        ></div>
        <div className="container">
          <h1>Our Services</h1>
          <p className="hero-description">
            <strong>UpNorth Impact</strong> provides comprehensive philanthropic advisory and management consulting for donors, foundations, and nonprofits. We bridge the gap between generous intentions and measurable results through a disciplined, four-pillar process.
          </p>
        </div>
      </section>

      <section className="services-content">
        <div
          className="services-content-background"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/topo_map.png)`
          }}
          aria-hidden="true"
        />
        <div className="container">
          <h2 className="services-section-title">How We Work: The UpNorth Impact Framework</h2>
          <div className="services-grid">
            {pillars.map((pillar, index) => (
              <article
                key={index}
                className="service-card service-card--pillar"
                data-step={index + 1}
              >
                <h3>{renderPillarTitle(pillar.title)}</h3>
                <p className="service-card-intro">{pillar.intro}</p>
                <ul className="service-card-list">
                  {pillar.bullets.map((item, i) => (
                    <li key={i}>
                      <span className="service-card-bullet-label">{item.label}:</span>{' '}
                      {item.text}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="services-cta">
            <h2>Ready to Get Started?</h2>
            <p>Let's discuss how we can help your organization achieve its goals.</p>
            <Link to="/contact" className="btn btn-primary btn-large">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
